import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  alpha,
} from '@mui/material';
import {
  Map as MapIcon,
  Timeline as TimelineIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  BubbleChart as BubbleChartIcon,
  Speed as GaugeIcon,
  DonutLarge as DonutIcon,
  AccountTree as TreeIcon,
  ShowChart as ShowChartIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { Chart } from 'react-google-charts';

// Helper function to convert hex color to rgba string for Google Charts
const hexToRgba = (hex, opacity = 1) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const googleColors = {
  primary: '#0078D4',
  secondary: '#40E0D0',
  success: '#107C10',
  warning: '#FFB900',
  danger: '#D13438',
  purple: '#5C2D91',
  orange: '#FF8C00',
  pink: '#E91E63',
  teal: '#009688',
  indigo: '#3F51B5',
  gradient: {
    blue: ['#667eea', '#764ba2'],
    sunset: ['#f093fb', '#f5576c'],
    ocean: ['#2E3192', '#1BFFFF'],
    forest: ['#134E5E', '#71B280'],
  },
  chart: [
    '#0078D4', '#40E0D0', '#107C10', '#FFB900', '#5C2D91', '#D13438',
    '#FF8C00', '#E91E63', '#009688', '#3F51B5', '#00BCD4', '#8BC34A',
  ],
};

// Data: Participants by Latin American country
const latamParticipantsData = [
  ['Country', 'Participants'],
  ['Argentina', 1250],
  ['Brazil', 890],
  ['Chile', 620],
  ['Colombia', 740],
  ['Mexico', 980],
  ['Peru', 450],
  ['Uruguay', 280],
  ['Ecuador', 310],
  ['Bolivia', 180],
  ['Paraguay', 150],
  ['Venezuela', 220],
  ['Costa Rica', 190],
  ['Panama', 160],
  ['Guatemala', 140],
  ['Honduras', 95],
  ['El Salvador', 110],
  ['Nicaragua', 85],
  ['Dominican Republic', 175],
];

const latamMapData = [
  ['Country', 'Participants', { role: 'tooltip', type: 'string', p: { html: true } }],
  ['AR', 1250, '<div style="padding:8px"><b>Argentina</b><br/>1,250 Participants<br/>23% of total</div>'],
  ['BR', 890, '<div style="padding:8px"><b>Brasil</b><br/>890 Participants<br/>16% of total</div>'],
  ['CL', 620, '<div style="padding:8px"><b>Chile</b><br/>620 Participants<br/>11% of total</div>'],
  ['CO', 740, '<div style="padding:8px"><b>Colombia</b><br/>740 Participants<br/>14% of total</div>'],
  ['MX', 980, '<div style="padding:8px"><b>México</b><br/>980 Participants<br/>18% of total</div>'],
  ['PE', 450, '<div style="padding:8px"><b>Perú</b><br/>450 Participants<br/>8% of total</div>'],
  ['UY', 280, '<div style="padding:8px"><b>Uruguay</b><br/>280 Participants<br/>5% of total</div>'],
  ['EC', 310, '<div style="padding:8px"><b>Ecuador</b><br/>310 Participants<br/>6% of total</div>'],
];

// Timeline data: Course progression
const timelineData = [
  ['Month', 'Enrolled', 'Active', 'Completed'],
  ['Jan', 420, 380, 180],
  ['Feb', 480, 420, 210],
  ['Mar', 520, 460, 260],
  ['Apr', 610, 540, 320],
  ['May', 680, 610, 410],
  ['Jun', 720, 660, 480],
];

// Pie chart: Student engagement levels
const engagementData = [
  ['Level', 'Students'],
  ['High Engagement', 580],
  ['Medium Engagement', 320],
  ['Low Engagement', 145],
  ['At Risk', 85],
];

// Bar chart: Performance by cohort
const cohortPerformanceData = [
  ['Cohort', 'Avg Score', 'Completion Rate'],
  ['Cohort 2024-Q1', 82, 88],
  ['Cohort 2024-Q2', 78, 85],
  ['Cohort 2024-Q3', 85, 91],
  ['Cohort 2024-Q4', 88, 94],
  ['Cohort 2025-Q1', 84, 89],
];

// Bubble chart: Course metrics
const courseBubbleData = [
  ['ID', 'Difficulty', 'Completion Rate', 'Course', 'Enrollment'],
  ['', 0, 0, '', 0], // Placeholder for proper axes
  ['C1', 65, 88, 'Introduction to Programming', 420],
  ['C2', 78, 75, 'Data Structures', 280],
  ['C3', 45, 92, 'Web Development Basics', 520],
  ['C4', 82, 68, 'Advanced Algorithms', 180],
  ['C5', 58, 85, 'Database Design', 340],
  ['C6', 72, 79, 'Machine Learning Intro', 260],
  ['C7', 38, 94, 'UI/UX Fundamentals', 480],
];

// Gauge chart: Overall satisfaction
const satisfactionGaugeData = [
  ['Label', 'Value'],
  ['NPS Score', 78],
];

// Donut chart: Learning modalities
const modalitiesData = [
  ['Modality', 'Students'],
  ['Synchronous Classes', 580],
  ['Async Video Learning', 420],
  ['Self-Paced Projects', 310],
  ['Mentorship Sessions', 190],
  ['Group Workshops', 280],
];

// Column chart: Monthly enrollments
const monthlyEnrollmentsData = [
  ['Month', 'New Enrollments', 'Re-enrollments'],
  ['Jan', 145, 42],
  ['Feb', 168, 38],
  ['Mar', 192, 55],
  ['Apr', 210, 62],
  ['May', 185, 48],
  ['Jun', 220, 71],
  ['Jul', 198, 58],
  ['Aug', 235, 82],
];

// Org chart: Academic structure
const orgChartData = [
  ['Name', 'Parent', 'Tooltip'],
  ['Academic Programs', '', 'Academic Programs Director'],
  ['Data Science', 'Academic Programs', 'Data Science Track'],
  ['Web Development', 'Academic Programs', 'Web Development Track'],
  ['UX Design', 'Academic Programs', 'UX Design Track'],
  ['Python Bootcamp', 'Data Science', '8 weeks - 420 students'],
  ['ML Foundations', 'Data Science', '12 weeks - 180 students'],
  ['Full Stack', 'Web Development', '16 weeks - 520 students'],
  ['Frontend Specialist', 'Web Development', '8 weeks - 340 students'],
  ['UX Research', 'UX Design', '6 weeks - 220 students'],
  ['UI Design', 'UX Design', '8 weeks - 280 students'],
];

// Scatter chart: Study time vs performance
const scatterData = [
  ['Study Hours/Week', 'Final Score'],
  [8, 72],
  [12, 78],
  [15, 82],
  [18, 88],
  [20, 85],
  [22, 91],
  [25, 94],
  [10, 74],
  [14, 79],
  [16, 83],
  [19, 86],
  [23, 90],
  [6, 68],
  [9, 73],
  [28, 96],
];

// Stepped area chart: Resource usage
const resourceUsageData = [
  ['Hour', 'Active Users'],
  ['00:00', 45],
  ['02:00', 28],
  ['04:00', 18],
  ['06:00', 52],
  ['08:00', 185],
  ['10:00', 420],
  ['12:00', 380],
  ['14:00', 520],
  ['16:00', 610],
  ['18:00', 580],
  ['20:00', 450],
  ['22:00', 280],
];

// Combo chart: Engagement metrics
const engagementMetricsData = [
  ['Week', 'Login Frequency', 'Avg Session (min)', 'Assignment Submissions'],
  ['Week 1', 420, 42, 380],
  ['Week 2', 480, 48, 420],
  ['Week 3', 520, 52, 460],
  ['Week 4', 460, 45, 410],
  ['Week 5', 510, 55, 480],
  ['Week 6', 580, 62, 520],
];

// Word Cloud: Educational Analytics Terms
const wordCloudData = [
  ['Term', 'Frequency'],
  ['Analytics', 45],
  ['Engagement', 38],
  ['Performance', 35],
  ['Learning', 42],
  ['Assessment', 28],
  ['Progress', 32],
  ['Achievement', 25],
  ['Participation', 30],
  ['Completion', 26],
  ['Interaction', 22],
  ['Collaboration', 20],
  ['Retention', 29],
  ['Success', 24],
  ['Improvement', 21],
  ['Skills', 27],
  ['Knowledge', 23],
  ['Understanding', 19],
  ['Mastery', 18],
  ['Growth', 31],
  ['Development', 33],
  ['Education', 36],
  ['Student', 40],
  ['Course', 34],
  ['Module', 22],
  ['Lesson', 20],
  ['Assignment', 25],
  ['Quiz', 18],
  ['Exam', 19],
  ['Grade', 21],
  ['Feedback', 24],
  ['Review', 17],
  ['Discussion', 16],
  ['Forum', 15],
  ['Question', 19],
  ['Answer', 18],
  ['Resource', 20],
  ['Material', 19],
  ['Content', 23],
  ['Video', 26],
  ['Tutorial', 21],
  ['Practice', 24],
  ['Exercise', 22],
  ['Project', 27],
  ['Portfolio', 18],
  ['Certificate', 20],
  ['Credential', 17],
  ['Competency', 19],
  ['Objective', 21],
  ['Outcome', 23],
  ['Metric', 28],
];

const ChartCard = ({ title, subtitle, children, icon: Icon, gradientColors }) => {
  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        background: gradientColors 
          ? `linear-gradient(135deg, ${alpha(gradientColors[0], 0.05)} 0%, ${alpha(gradientColors[1], 0.05)} 100%)`
          : '#FFFFFF',
        borderRadius: 3,
        border: `1px solid ${alpha(googleColors.primary, 0.12)}`,
        boxShadow: '0 8px 32px rgba(0, 120, 212, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: gradientColors
            ? `linear-gradient(90deg, ${gradientColors[0]}, ${gradientColors[1]})`
            : `linear-gradient(90deg, ${googleColors.primary}, ${googleColors.secondary})`,
        },
        '&:hover': {
          boxShadow: '0 16px 48px rgba(0, 120, 212, 0.12)',
          transform: 'translateY(-4px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {Icon && (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              background: gradientColors
                ? `linear-gradient(135deg, ${alpha(gradientColors[0], 0.15)}, ${alpha(gradientColors[1], 0.15)})`
                : `linear-gradient(135deg, ${alpha(googleColors.primary, 0.15)}, ${alpha(googleColors.secondary, 0.15)})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 24, color: gradientColors?.[0] || googleColors.primary }} />
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {children}
    </Paper>
  );
};

const GoogleChartsShowcase = () => {
  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      p: { xs: 2, md: 4 }
    }}>
      {/* Hero Header */}
      <Box sx={{ mb: 5, textAlign: 'center' }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 800, 
            mb: 2,
            background: `linear-gradient(135deg, ${googleColors.primary} 0%, ${googleColors.purple} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Google Charts Showcase
        </Typography>
        <Typography variant="h6" sx={{ color: '#666', fontWeight: 400, mb: 3 }}>
          Interactive data visualizations powered by Google Charts - Educational Analytics Focus
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Chip label="GeoChart" color="primary" />
          <Chip label="Timeline" sx={{ bgcolor: googleColors.secondary, color: 'white' }} />
          <Chip label="Gauges" sx={{ bgcolor: googleColors.success, color: 'white' }} />
          <Chip label="Org Chart" sx={{ bgcolor: googleColors.warning, color: 'white' }} />
          <Chip label="Bubble Chart" sx={{ bgcolor: googleColors.purple, color: 'white' }} />
          <Chip label="Combo Charts" sx={{ bgcolor: googleColors.danger, color: 'white' }} />
        </Box>
      </Box>

      {/* Section 1: Geographic Distribution */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Geographic Distribution
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={8}>
          <ChartCard
            title="Latin America Participants Map"
            subtitle="Interactive map showing participant distribution across LATAM"
            icon={MapIcon}
            gradientColors={googleColors.gradient.blue}
          >
            <Chart
              chartType="GeoChart"
              width="100%"
              height="500px"
              data={latamMapData}
              options={{
                region: '019', // Latin America region code
                colorAxis: { 
                  colors: ['#C0F5ED', googleColors.secondary, googleColors.primary, googleColors.purple],
                  minValue: 0,
                  maxValue: 1300
                },
                backgroundColor: '#f8f9fa',
                datalessRegionColor: '#e9ecef',
                defaultColor: '#A0E7E0',
                tooltip: { isHtml: true, textStyle: { fontSize: 13 } },
                legend: { textStyle: { fontSize: 12 } },
              }}
            />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Top Countries"
            subtitle="Participants by country"
            icon={BarChartIcon}
            gradientColors={googleColors.gradient.ocean}
          >
            <Chart
              chartType="BarChart"
              width="100%"
              height="500px"
              data={latamParticipantsData}
              options={{
                colors: [googleColors.primary],
                legend: { position: 'none' },
                hAxis: { 
                  title: 'Participants',
                  minValue: 0,
                  textStyle: { fontSize: 11 }
                },
                vAxis: { 
                  textStyle: { fontSize: 11 }
                },
                chartArea: { width: '70%', height: '80%' },
              }}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 2: Performance Metrics */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Performance & Satisfaction
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={4}>
          <ChartCard
            title="NPS Satisfaction Gauge"
            subtitle="Overall student satisfaction score"
            icon={GaugeIcon}
            gradientColors={[googleColors.success, googleColors.warning]}
          >
            <Chart
              chartType="Gauge"
              width="100%"
              height="300px"
              data={satisfactionGaugeData}
              options={{
                redFrom: 0,
                redTo: 40,
                yellowFrom: 40,
                yellowTo: 65,
                greenFrom: 65,
                greenTo: 100,
                minorTicks: 5,
                max: 100,
              }}
            />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={8}>
          <ChartCard
            title="Cohort Performance Comparison"
            subtitle="Average scores and completion rates by cohort"
            icon={BarChartIcon}
            gradientColors={googleColors.gradient.forest}
          >
            <Chart
              chartType="ColumnChart"
              width="100%"
              height="300px"
              data={cohortPerformanceData}
              options={{
                colors: [googleColors.primary, googleColors.success],
                legend: { position: 'bottom' },
                hAxis: { 
                  textStyle: { fontSize: 11 }
                },
                vAxis: { 
                  title: 'Percentage',
                  minValue: 0,
                  maxValue: 100,
                  textStyle: { fontSize: 11 }
                },
                chartArea: { width: '80%', height: '65%' },
              }}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 3: Student Distribution */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Student Engagement & Distribution
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Engagement Levels"
            subtitle="Student distribution by engagement"
            icon={PieChartIcon}
            gradientColors={[googleColors.purple, googleColors.pink]}
          >
            <Chart
              chartType="PieChart"
              width="100%"
              height="400px"
              data={engagementData}
              options={{
                colors: [googleColors.success, googleColors.primary, googleColors.warning, googleColors.danger],
                pieSliceText: 'percentage',
                legend: { position: 'bottom', textStyle: { fontSize: 12 } },
                chartArea: { width: '90%', height: '75%' },
                pieHole: 0.4,
                sliceVisibilityThreshold: 0,
              }}
            />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Learning Modalities"
            subtitle="Distribution by learning type"
            icon={DonutIcon}
            gradientColors={[googleColors.orange, googleColors.pink]}
          >
            <Chart
              chartType="PieChart"
              width="100%"
              height="400px"
              data={modalitiesData}
              options={{
                colors: googleColors.chart.slice(0, 5),
                pieSliceText: 'value',
                legend: { position: 'right', textStyle: { fontSize: 12 } },
                chartArea: { width: '90%', height: '85%' },
                pieHole: 0.5,
              }}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 4: Course Analysis */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Course Insights
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <ChartCard
            title="Course Metrics: Difficulty vs Completion"
            subtitle="Bubble size represents enrollment numbers"
            icon={BubbleChartIcon}
            gradientColors={[googleColors.pink, googleColors.purple]}
          >
            <Chart
              chartType="BubbleChart"
              width="100%"
              height="450px"
              data={courseBubbleData}
              options={{
                colors: [googleColors.primary],
                hAxis: { 
                  title: 'Difficulty Level (0-100)',
                  minValue: 30,
                  maxValue: 90,
                  textStyle: { fontSize: 11 }
                },
                vAxis: { 
                  title: 'Completion Rate (%)',
                  minValue: 60,
                  maxValue: 100,
                  textStyle: { fontSize: 11 }
                },
                bubble: { 
                  textStyle: { fontSize: 10, color: 'transparent' },
                  opacity: 0.7
                },
                chartArea: { width: '85%', height: '75%' },
                legend: { position: 'none' },
              }}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 5: Organizational Structure */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Program Structure
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <ChartCard
            title="Academic Programs Organization"
            subtitle="Program hierarchy with enrollment numbers"
            icon={TreeIcon}
            gradientColors={googleColors.gradient.blue}
          >
            <Chart
              chartType="OrgChart"
              width="100%"
              height="500px"
              data={orgChartData}
              options={{
                allowHtml: true,
                allowCollapse: true,
                nodeClass: 'org-node',
                selectedNodeClass: 'org-node-selected',
                size: 'medium',
              }}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 6: Trends */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Trends & Time Series
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Course Progression Timeline"
            subtitle="Enrollment, active and completed students over 6 months"
            icon={TimelineIcon}
            gradientColors={googleColors.gradient.sunset}
          >
            <Chart
              chartType="LineChart"
              width="100%"
              height="400px"
              data={timelineData}
              options={{
                colors: [googleColors.primary, googleColors.warning, googleColors.success],
                curveType: 'function',
                legend: { position: 'bottom', textStyle: { fontSize: 12 } },
                hAxis: { 
                  textStyle: { fontSize: 11 }
                },
                vAxis: { 
                  title: 'Students',
                  minValue: 0,
                  textStyle: { fontSize: 11 }
                },
                chartArea: { width: '85%', height: '70%' },
                pointSize: 5,
              }}
            />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Monthly Enrollments"
            subtitle="New vs re-enrollments trend"
            icon={BarChartIcon}
            gradientColors={googleColors.gradient.ocean}
          >
            <Chart
              chartType="ColumnChart"
              width="100%"
              height="400px"
              data={monthlyEnrollmentsData}
              options={{
                colors: [googleColors.primary, googleColors.secondary],
                isStacked: true,
                legend: { position: 'bottom', textStyle: { fontSize: 12 } },
                hAxis: { 
                  textStyle: { fontSize: 11 }
                },
                vAxis: { 
                  title: 'Enrollments',
                  minValue: 0,
                  textStyle: { fontSize: 11 }
                },
                chartArea: { width: '85%', height: '70%' },
              }}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 7: Correlations */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Correlations & Patterns
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Study Time vs Performance"
            subtitle="Correlation between weekly study hours and final scores"
            icon={ShowChartIcon}
            gradientColors={[googleColors.teal, googleColors.indigo]}
          >
            <Chart
              chartType="ScatterChart"
              width="100%"
              height="400px"
              data={scatterData}
              options={{
                colors: [googleColors.primary],
                hAxis: { 
                  title: 'Study Hours per Week',
                  minValue: 5,
                  maxValue: 30,
                  textStyle: { fontSize: 11 }
                },
                vAxis: { 
                  title: 'Final Score',
                  minValue: 60,
                  maxValue: 100,
                  textStyle: { fontSize: 11 }
                },
                legend: { position: 'none' },
                chartArea: { width: '80%', height: '75%' },
                pointSize: 7,
                trendlines: { 
                  0: { 
                    type: 'linear',
                    color: googleColors.danger,
                    lineWidth: 2,
                    opacity: 0.5,
                    showR2: true,
                    visibleInLegend: true
                  } 
                },
              }}
            />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Platform Activity by Hour"
            subtitle="Active users throughout the day"
            icon={TimelineIcon}
            gradientColors={googleColors.gradient.forest}
          >
            <Chart
              chartType="SteppedAreaChart"
              width="100%"
              height="400px"
              data={resourceUsageData}
              options={{
                colors: [googleColors.primary],
                legend: { position: 'none' },
                hAxis: { 
                  title: 'Time of Day',
                  textStyle: { fontSize: 10 }
                },
                vAxis: { 
                  title: 'Active Users',
                  minValue: 0,
                  textStyle: { fontSize: 11 }
                },
                chartArea: { width: '85%', height: '70%' },
                areaOpacity: 0.4,
              }}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 8: Combo Charts */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Multi-Metric Analysis
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <ChartCard
            title="Weekly Engagement Metrics"
            subtitle="Login frequency, session duration, and assignment submissions"
            icon={AssessmentIcon}
            gradientColors={[googleColors.indigo, googleColors.teal]}
          >
            <Chart
              chartType="ComboChart"
              width="100%"
              height="400px"
              data={engagementMetricsData}
              options={{
                colors: [googleColors.primary, googleColors.warning, googleColors.success],
                seriesType: 'bars',
                series: { 
                  1: { type: 'line', targetAxisIndex: 1 } 
                },
                legend: { position: 'bottom', textStyle: { fontSize: 12 } },
                hAxis: { 
                  textStyle: { fontSize: 11 }
                },
                vAxes: {
                  0: { 
                    title: 'Count',
                    textStyle: { fontSize: 11 }
                  },
                  1: { 
                    title: 'Average Session (minutes)',
                    textStyle: { fontSize: 11 }
                  }
                },
                chartArea: { width: '85%', height: '70%' },
              }}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Footer info */}
      <Box sx={{ mt: 6, p: 4, textAlign: 'center', bgcolor: 'rgba(255, 255, 255, 0.9)', borderRadius: 3, border: `1px solid ${alpha(googleColors.primary, 0.1)}` }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: googleColors.primary }}>
          📊 Google Charts
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Powerful, simple to use, and free data visualization library. Perfect for educational analytics with built-in interactivity.
          All data shown is synthetic and generated for demonstration purposes only.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
          <Chip label="Interactive Maps" size="small" color="primary" />
          <Chip label="Gauges & KPIs" size="small" sx={{ bgcolor: googleColors.success, color: 'white' }} />
          <Chip label="Org Charts" size="small" sx={{ bgcolor: googleColors.warning, color: 'white' }} />
          <Chip label="Combo Charts" size="small" sx={{ bgcolor: googleColors.purple, color: 'white' }} />
          <Chip label="Free to Use" size="small" sx={{ bgcolor: googleColors.secondary, color: 'white' }} />
        </Box>
      </Box>
    </Box>
  );
};

export default GoogleChartsShowcase;

